import { useQueryClient } from '@tanstack/react-query'
import { CirclePlus, Search } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Dropzone } from '@/components/ui/dropzone'
import { Input } from '@/components/ui/input'
import { TablePagination } from '@/components/ui/pagination'
import { Separator } from '@/components/ui/separator'
import { SkeletonRows } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useDebouncedInput } from '../../../../hooks/useDebouncedInput'
import { CacheKeys } from '../../../../lib/cache'
import { PARSER_ACCEPTED_FILE_TYPES } from '../../../../lib/fileUpload'
import { formatDate } from '../../../../lib/utils'
import { formatAddress } from '../../../../models/address'
import {
  type COICertificateHolder,
  useCertificateHolder,
  useCertificateHolders,
} from '../../../../models/coi'
import { useCurrentUser } from '../../../../models/user'
import { AddCertificateHolderDialog } from './AddCertificateHolderDialog'
import { CertificateHolderDrawer } from './CertificateHolderDrawer'
import { CertificateHolderRowActions } from './CertificateHolderRowActions'
import { EndorsementWarning } from './EndorsementWarning'
import { useCertificateHolderImport } from './useCertificateHolderImport'

/** Which certificate this holder receives, by the policies it states. */
function holderCertificateLabel(holder: COICertificateHolder): string {
  const policies = holder.policies ?? []
  if (!policies.length) return '—'
  return policies.map((policy) => policy.vendorName || policy.label).join(' + ')
}

export function CertificateHoldersTab({ orgId, manualMode }: { orgId?: string; manualMode?: boolean }) {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const { user } = useCurrentUser({ enabled: true })
  const isCaiAdmin = !!user?.isCaiAdmin
  const queryClient = useQueryClient()
  const canImport = !manualMode

  const page = Math.max(1, Number(searchParams.get('page')) || 1)
  const search = searchParams.get('search') ?? ''
  // The hook refreshes this list itself while the background import runs.
  const { importFile, isUploading, isImporting } = useCertificateHolderImport(orgId)
  const { holders, count, hasNextPage, hasPreviousPage, isLoading, isFetching } = useCertificateHolders(
    orgId,
    page,
    search
  )
  // Searching is only worth the screen space once the list outgrows one page, but
  // it has to stay put while a search is active — narrowing to a single page of
  // results must not remove the box you would clear the search in.
  const isPaged = hasNextPage || hasPreviousPage
  const showSearch = isPaged || !!search

  function updateParams(mutate: (params: URLSearchParams) => void, replace = false) {
    const next = new URLSearchParams(searchParams)
    mutate(next)
    // navigate, not setSearchParams: the latter navigates to the search string
    // alone and drops the hash, and the hash is what selects the tab — both this
    // page's own tabs and the customer-detail tab group it renders inside
    // (CustomerDetail reads location.hash). Losing it navigated away from the COI
    // tab entirely on every page change.
    navigate({ search: next.toString(), hash: location.hash }, { replace, preventScrollReset: true })
  }

  function goToPage(nextPage: number) {
    updateParams((params) => {
      if (nextPage <= 1) params.delete('page')
      else params.set('page', String(nextPage))
    })
  }

  const { inputValue, setInputValue } = useDebouncedInput(search, (value) =>
    updateParams((params) => {
      if (value) params.set('search', value)
      else params.delete('search')
      // The old page number means nothing against a different result set.
      params.delete('page')
    })
  )

  // Deleting the last holder on a page leaves it out of range, which 404s.
  useEffect(() => {
    if (!isLoading && page > 1 && holders.length === 0) goToPage(1)
  }, [isLoading, page, holders.length])

  // Deep link from e.g. the "COI requested" sales notification. The named holder
  // can be on any page of thousands, so open its drawer rather than trying to
  // find its row — the drawer fetches the holder by id either way.
  const holderParam = searchParams.get('holder')
  useEffect(() => {
    if (!holderParam) return
    setSelectedId(holderParam)
    updateParams((params) => params.delete('holder'), true)
  }, [holderParam])

  const holderOnPage = holders.find((h) => h.id === selectedId) ?? null
  // Only fetched when the selected holder is not in the current page's results.
  const { holder: fetchedHolder } = useCertificateHolder(
    selectedId && !holderOnPage ? selectedId : null,
    orgId
  )
  const selectedHolder = holderOnPage ?? fetchedHolder

  function handleDialogSuccess() {
    queryClient.invalidateQueries({ queryKey: [CacheKeys.COI_CERTIFICATE_HOLDERS] })
  }

  function renderImportingNotice() {
    if (!isImporting) return null
    return (
      <div className="text-muted-foreground flex items-center gap-2 text-sm">
        <Spinner className="size-4" />
        Importing certificate holders — they appear here as they are added.
      </div>
    )
  }

  function renderSearch() {
    if (!showSearch) return null
    return (
      <div className="relative max-w-sm flex-1">
        {/* Swaps to a spinner in place while results load, so the control keeps
            its position and its focus instead of the row reflowing. */}
        {isFetching ? (
          <Spinner className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        ) : (
          <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        )}
        <Input
          placeholder="Search by name, email or address…"
          aria-label="Search certificate holders"
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          className="pl-9"
        />
      </div>
    )
  }

  // Only the very first load, when there is nothing to show yet. Later loads keep
  // the previous results on screen (keepPreviousData), so the search box is never
  // unmounted mid-search — doing that dropped the caret after one keystroke. The
  // box still renders here for a cold load that already has a search in the URL.
  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        {renderSearch()}
        <SkeletonRows rows={3} />
      </div>
    )
  }

  // An empty result set with a search active is "nothing matched", not "no
  // holders yet" — offering the importer here would be answering the wrong
  // question, and would hide the box the search is typed into.
  if (holders.length === 0 && search) {
    return (
      <div className="flex flex-col gap-4">
        {renderSearch()}
        <p className="text-muted-foreground py-10 text-center">
          No certificate holders match &ldquo;{search}&rdquo;.
        </p>
      </div>
    )
  }

  if (holders.length === 0) {
    return (
      <Card className="border-none shadow-none">
        <CardContent>
          <div className="mt-10 mb-10 flex w-full flex-col items-center justify-center gap-6">
            <p className="text-2xl font-semibold text-neutral-400">No certificate holders were added.</p>

            <Button variant="default" onClick={() => setDialogOpen(true)}>
              <CirclePlus className="size-4" />{' '}
              {manualMode && !isCaiAdmin ? 'Request certificate holder' : 'Add certificate holder'}
            </Button>

            {canImport && (
              <>
                <div className="text-muted-foreground flex items-center justify-center gap-2 text-sm">
                  <Separator className="w-[100px]! shrink" /> or <Separator className="w-[100px]! shrink" />
                </div>

                <Dropzone
                  onChange={importFile}
                  accept={PARSER_ACCEPTED_FILE_TYPES}
                  maxSizeMB={10}
                  isLoading={isUploading}
                  key={`${holders.length}-${isUploading}`}
                />
                {renderImportingNotice()}
                <div className="mx-auto mt-10 max-w-[488px]">
                  <EndorsementWarning />
                </div>
              </>
            )}
          </div>

          <AddCertificateHolderDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            onSuccess={handleDialogSuccess}
            orgId={orgId}
            manualMode={manualMode}
          />
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-4">
        {renderSearch()}
        {renderImportingNotice()}
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="font-semibold">Name</TableHead>
            <TableHead className="font-semibold">Email</TableHead>
            <TableHead className="font-semibold">Address</TableHead>
            <TableHead className="font-semibold">Certificate</TableHead>
            <TableHead className="font-semibold">Last sent</TableHead>
            <TableHead className="font-semibold"></TableHead>
            <TableHead className="font-semibold"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {holders.map((holder: COICertificateHolder) => (
            <TableRow
              key={holder.id}
              id={`holder-row-${holder.id}`}
              className="hover:bg-muted/50 cursor-pointer"
              onClick={() => setSelectedId(holder.id)}
            >
              <TableCell className="font-medium">{holder.name}</TableCell>
              <TableCell>{holder.email}</TableCell>
              <TableCell>{formatAddress(holder.address)}</TableCell>
              <TableCell>{holderCertificateLabel(holder)}</TableCell>
              <TableCell>{formatDate(holder.dateLastSent)}</TableCell>
              <TableCell>
                {!holder.isAutomatic && !holder.hasDocument ? (
                  <span className="font-semibold text-amber-700">Requested</span>
                ) : (
                  <span className="text-accent-green font-semibold">
                    {holder.hasLatestVersionSent ? 'Latest COI' : ''}
                  </span>
                )}
              </TableCell>
              {/* The row actions open their own dialogs — a click on one must not
                  also open the drawer behind them. */}
              <TableCell onClick={(event) => event.stopPropagation()}>
                <CertificateHolderRowActions holder={holder} orgId={orgId} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        page={page}
        count={count}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        onPageChange={goToPage}
        itemLabel="certificate holder"
      />

      <CertificateHolderDrawer
        holder={selectedHolder}
        open={!!selectedId}
        onOpenChange={(isOpen) => {
          if (!isOpen) setSelectedId(null)
        }}
        orgId={orgId}
      />
    </div>
  )
}
